import classNames from "classnames";
import isArray from "lodash/isArray";
import { SidePanelType, useSidePanelActions } from "PFApp/stores/side_panels_store";
import { Button } from "PFComponents/button";
import { Flex, Inline, Stack } from "PFComponents/containers";
import SidePanel from "PFComponents/side_panel/side_panel";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import { Typography } from "PFComponents/typography";
import { ReactNode, RefObject, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "../../../tabular_roles_form.module.scss";

type SidePanelCellFormSectionProps = {
  label: string | string[];
  sidePanelTitle: string;
  disabled?: boolean;
  children?: ReactNode;
  error?: string;
  buttonText?: string;
  editAriaLabel?: string;
  cellAriaLabel?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const SidePanelCellFormSection = ({
  label,
  sidePanelTitle,
  disabled,
  children,
  error,
  buttonText,
  editAriaLabel,
  cellAriaLabel,
  inputFocusProps,
  buttonRef
}: SidePanelCellFormSectionProps) => {
  const { t } = useTranslation();
  const { close: closeCustomValuePreview } = useSidePanelActions(SidePanelType.CustomValuePreview);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
  };

  const handleOpenSidePanel = () => {
    closeCustomValuePreview();
    setIsSidePanelOpen(true);
  };

  const footerRenderer = ({ onSidePanelClose }: { onSidePanelClose: () => void }) => (
    <Inline gap="spacingMd" justifyContent="end">
      <Button kind="primary" onClick={onSidePanelClose} text={t("done")} />
    </Inline>
  );

  return (
    <Flex
      className={classNames(css.cellFormSection, { [css.error]: error })}
      aria-label={cellAriaLabel}
      style={inputFocusProps?.style}
    >
      <Stack gap="spacingSm" justifyContent="start">
        {(isArray(label) ? label : [label]).map((text, index) => (
          <Typography key={index} variant="bodyRegular">
            {text}
          </Typography>
        ))}
        {children && (
          <Button
            ref={buttonRef}
            kind="text"
            className={css.editButton}
            onClick={handleOpenSidePanel}
            text={buttonText || t("edit")}
            aria-label={editAriaLabel}
            disabled={disabled}
            tabIndex={inputFocusProps?.tabIndex}
          />
        )}
      </Stack>

      {children && (
        <SidePanel
          show={isSidePanelOpen && !disabled}
          onClose={handleCloseSidePanel}
          title={sidePanelTitle}
          footerRenderer={footerRenderer}
          fullHeight
          closeOnClickOutside
          zIndex={10}
        >
          {children}
        </SidePanel>
      )}
    </Flex>
  );
};
